import type { ConnectorId, ConnectorSnapshot } from "./connectors/types";
import {
  buildProjectionSets,
  getProjectionById as findProjectionById,
  type ProjectionSet,
  type ProjectionTrajectory,
} from "./projections";
import {
  getSimulationModel,
  getSimulationRun,
  runSimulationModels,
  simulationModels,
  type SimulationModel,
  type SimulationRun,
} from "./simulations";
import { systems, type SystemId, type SystemRecord } from "./systems";
import type { TimelineEvidenceEvent } from "./timeline-types";
import { buildTimelineSequences, type TimelineLens } from "./timeline-lenses";
import type { PortfolioPosition } from "./portfolio";
import type { MediaDraft } from "./media";
import { signalTimelineEvents, type SignalPacket } from "./signal-graph";

export type KnowledgeKind = "source" | "signal" | "claim" | "system" | "simulation" | "projection" | "timeline" | "portfolio" | "media";

export type KnowledgeRecord = {
  id: string;
  kind: KnowledgeKind;
  entityId: string;
  title: string;
  subtitle: string;
  body: string;
  url?: string;
  navigation: {
    view: "sources" | "systems" | "simulations" | "timeline" | "portfolio" | "media";
    timelineMode?: "observed" | "verification" | "projected";
    timelineLens?: TimelineLens;
    targetId?: string;
  };
};

export type KnowledgeSearchOptions = {
  kinds?: KnowledgeKind[];
  limit?: number;
  offset?: number;
};

export type KnowledgeSearchResult = {
  query: string;
  total: number;
  offset: number;
  limit: number;
  results: KnowledgeRecord[];
};

function compact(parts: Array<string | null | undefined>) {
  return parts.filter(Boolean).join(" · ");
}

export function deriveObservedTimelineEvents(
  connectors: ConnectorSnapshot[],
): TimelineEvidenceEvent[] {
  return buildTimelineSequences(connectors, [], systems).unified;
}

function connectorBody(connector: ConnectorSnapshot) {
  const parts = [
    connector.name,
    connector.category,
    connector.kind,
    connector.status,
    connector.instrument,
    connector.headline,
    connector.primary.label,
    connector.primary.value,
    connector.secondary.label,
    connector.secondary.value,
    connector.detail,
  ];

  connector.person?.profiles.forEach((profile) => {
    parts.push(profile.platform, profile.label, profile.url);
  });
  if (connector.person) {
    parts.push(connector.person.group, connector.person.role, connector.person.handle ?? "");
    parts.push(
      connector.person.incentives.status,
      connector.person.incentives.asOf,
      ...connector.person.incentives.roles.flatMap((role) => [
        role.title,
        role.institutionName,
      ]),
      ...connector.person.incentives.financialExposures.flatMap((exposure) => [
        exposure.targetName,
        exposure.relationship,
        exposure.scope,
        exposure.statement,
      ]),
      ...connector.person.incentives.informationProximity.flatMap((proximity) => [
        proximity.targetName,
        proximity.basis,
        proximity.statement,
        proximity.limitation,
      ]),
      ...connector.person.incentives.evidence.flatMap((evidence) => [
        evidence.label,
        evidence.url,
      ]),
      ...connector.person.incentives.unknowns,
    );
  }

  if (connector.incentiveModel) {
    const model = connector.incentiveModel;
    parts.push(
      model.actorKind,
      model.researchDepth,
      model.status,
      model.asOf,
      model.thesis,
      model.netWorth.headline,
      model.netWorth.status,
      ...model.positions.flatMap((position) => [
        position.targetName,
        position.role,
        position.state,
        ...position.systemIds,
        ...position.candidateSystemNames,
        ...position.authorityLevers,
        position.limitation,
      ]),
      ...model.economicTies.flatMap((tie) => [
        tie.targetName,
        tie.type,
        tie.scope,
        tie.direction,
        tie.statement,
        tie.limitation,
      ]),
      ...model.incentiveFactors.flatMap((factor) => [
        factor.category,
        factor.label,
        factor.description,
        factor.effect,
        ...factor.countervailingFactors,
      ]),
      ...model.netWorth.observableGrossAssetMarks.flatMap((mark) => [
        mark.label,
        mark.methodology,
        mark.caveat,
      ]),
      ...model.netWorth.excludedFromPersonalNetWorth,
      ...model.netWorth.unknownComponents,
      ...model.evidence.flatMap((evidence) => [evidence.label, evidence.url, evidence.note]),
      ...model.unknowns,
      ...model.conflicts,
    );
  }

  connector.institution?.officialSources.forEach((source) => {
    parts.push(source.type, source.label, source.url);
  });
  if (connector.institution) {
    parts.push(connector.institution.type, connector.institution.focus);
  }

  connector.media?.officialChannels?.forEach((channel) => {
    parts.push(channel.type, channel.label, channel.url);
  });
  connector.media?.items.forEach((item) => {
    parts.push(item.type, item.title, item.publishedAt, item.url);
  });

  return parts.filter(Boolean).join("\n");
}

function systemBody(system: SystemRecord) {
  return [
    system.name,
    system.type,
    system.scope,
    system.purpose,
    system.boundary.inside,
    system.boundary.outside,
    ...system.constituents,
    ...system.mechanisms.flatMap((mechanism) => [mechanism.name, mechanism.description]),
    ...system.stateVariables,
    ...system.stocks,
    ...system.flows,
    ...system.rules,
    ...system.dependencies,
    ...system.feedbackLoops.flatMap((loop) => [loop.name, loop.cycle, loop.delay]),
    ...system.indicators,
    ...system.sourceLinks.map((source) => source.label),
    ...system.coverageGaps,
    system.incentiveStructures?.length ? "Incentive structures" : "",
    ...(system.incentiveStructures ?? []).flatMap((incentive) => [
      incentive.actorClass,
      ...incentive.objectiveHypotheses,
      ...incentive.constraints,
      ...incentive.evidenceNeeded,
    ]),
  ].join("\n");
}

function simulationBody(model: SimulationModel, run: SimulationRun | undefined) {
  return [
    model.name,
    model.kind,
    model.version,
    model.purpose,
    model.target,
    model.partitionRule,
    model.method,
    model.equation,
    `${model.horizonDays} days`,
    `${model.particleCount} particles`,
    ...model.systemIds,
    ...model.requiredSourceIds,
    ...model.sourceGaps,
    ...model.stateVariables,
    ...model.parameters.flatMap((parameter) => [
      parameter.symbol,
      parameter.label,
      parameter.value,
      parameter.provenance,
    ]),
    ...model.outcomes.flatMap((outcome) => [
      outcome.title,
      outcome.summary,
      ...outcome.supportingConditions,
      ...outcome.invalidatingConditions,
    ]),
    ...model.limitations,
    run ? `${run.modelConfidence} model confidence` : "",
    ...(run?.outcomes.flatMap((outcome) => [outcome.title, `${outcome.probabilityBps / 100}%`]) ?? []),
  ].join("\n");
}

function projectionBody(set: ProjectionSet, trajectory?: ProjectionTrajectory) {
  const trajectories = trajectory ? [trajectory] : set.trajectories;
  return [
    set.family,
    set.title,
    set.target,
    set.partitionRule,
    `${set.horizonDays} days`,
    `${set.modelConfidence} model confidence`,
    `${set.calibration.status} calibration`,
    ...set.systemIds,
    ...set.requiredSourceIds,
    ...set.sourceGaps,
    ...trajectories.flatMap((candidate) => [
      candidate.title,
      candidate.summary,
      `${candidate.probabilityBps / 100}% probability`,
      ...candidate.supportingConditions,
      ...candidate.invalidatingConditions,
      ...candidate.checkpoints.flatMap((node) => [
      node.kind,
      node.title,
      node.detail,
      node.mechanism,
      ...node.stateVariables,
      ]),
    ]),
    ...set.tradeCandidates.flatMap((candidate) => [
      candidate.action,
      candidate.instrument,
      candidate.status,
      candidate.thesis,
      candidate.marketEdge,
    ]),
  ].join("\n");
}

function portfolioBody(position: PortfolioPosition) {
  return [
    "portfolio position",
    `origin ${position.origin}`,
    position.externalAccountLabel ? `account ${position.externalAccountLabel}` : "owner-entered record",
    position.syncedAt ? `synchronized ${position.syncedAt}` : "not broker synchronized",
    position.symbol,
    position.name,
    position.assetType,
    position.side,
    position.status,
    position.venue,
    position.thesis,
    position.notes,
    `${position.quantity} units`,
    position.unitCost === null ? "cost basis unknown" : `${position.unitCost} unit cost ${position.currency}`,
    position.markPrice === null ? "mark unknown" : `${position.markPrice} current mark ${position.currency}`,
    position.riskLimitPct === null ? "risk limit unset" : `${position.riskLimitPct}% maximum gross weight`,
    ...position.sourceIds,
    ...position.systemIds,
    ...position.simulationIds,
    ...position.projectionIds,
  ].join("\n");
}

function signalBody(packet: SignalPacket) {
  return [
    "signal packet",
    packet.id,
    packet.episode.sourceName,
    packet.episode.title,
    packet.episode.publishedAt,
    `${packet.claims.length} claims`,
    `${packet.markets.length} point-in-time market links`,
    ...packet.claims.map((claim) => claim.text),
    ...packet.entities.map((entity) => `${entity.label} ${entity.rationale}`),
    ...packet.systems.map((system) => `${system.name} ${system.rationale}`),
    ...packet.markets.map((market) => `${market.platform} ${market.title} ${market.probability ?? "probability unavailable"} observed ${market.observedAt}`),
    ...packet.simulations.map((simulation) => `${simulation.modelId} ${simulation.conditioning} ${simulation.rationale}`),
    ...packet.projections.map((projection) => `${projection.projectionId} ${projection.rationale}`),
    ...packet.limitations,
  ].join("\n");
}

function claimBody(packet: SignalPacket, claim: SignalPacket["claims"][number]) {
  const markets = packet.markets.filter((market) => market.claimIds.includes(claim.id));
  const linkedSystems = packet.systems.filter((system) => system.claimIds.includes(claim.id));
  const simulations = packet.simulations.filter((simulation) => simulation.claimIds.includes(claim.id));
  return [
    claim.kind,
    claim.text,
    claim.speaker.name,
    claim.speaker.role,
    claim.speaker.status,
    claim.modality,
    claim.review.status,
    claim.review.confidence,
    claim.derivation,
    ...claim.assumptions,
    ...claim.entities,
    ...claim.marketQuestions,
    ...claim.evidence.flatMap((evidence) => [evidence.quote, evidence.sourceUrl]),
    claim.timeline.kind,
    claim.timeline.startDate ?? "",
    claim.timeline.endDate,
    claim.timeline.rationale,
    claim.sourceAssessment.rationale,
    ...claim.sourceAssessment.limitations,
    ...linkedSystems.map((system) => `${system.name} ${system.rationale}`),
    ...markets.map((market) => `${market.platform} ${market.title} observed ${market.observedAt} probability ${market.probability ?? "unavailable"}`),
    ...simulations.map((simulation) => `${simulation.modelId} ${simulation.conditioning}`),
  ].join("\n");
}

export function buildKnowledgeRecords(
  connectors: ConnectorSnapshot[],
  timelineEvents?: TimelineEvidenceEvent[],
  modeling?: {
    simulationRuns: SimulationRun[];
    projectionSets: ProjectionSet[];
    portfolioPositions?: PortfolioPosition[];
    signalPackets?: SignalPacket[];
    mediaDrafts?: MediaDraft[];
  },
): KnowledgeRecord[] {
  const signalPackets = modeling?.signalPackets ?? [];
  const indexedTimelineEvents = timelineEvents ?? [
    ...deriveObservedTimelineEvents(connectors),
    ...signalTimelineEvents(signalPackets),
  ];
  const simulationRuns = modeling?.simulationRuns
    ?? runSimulationModels(connectors, indexedTimelineEvents);
  const projectionSets = modeling?.projectionSets
    ?? buildProjectionSets(simulationRuns);
  const portfolioPositions = modeling?.portfolioPositions ?? [];
  const mediaDrafts = modeling?.mediaDrafts ?? [];
  const sourceRecords: KnowledgeRecord[] = connectors.map((connector) => ({
    id: `source:${connector.id}`,
    kind: "source",
    entityId: connector.id,
    title: connector.name,
    subtitle: compact([
      connector.category,
      connector.kind,
      connector.status === "live" ? "connected" : connector.status.replaceAll("_", " "),
    ]),
    body: connectorBody(connector),
    url: connector.docsUrl,
    navigation: {
      view: "sources",
      targetId: `source-${connector.id}`,
    },
  }));

  const signalRecords: KnowledgeRecord[] = signalPackets.map((packet) => ({
    id: `signal:${packet.id}`,
    kind: "signal",
    entityId: packet.id,
    title: packet.episode.title,
    subtitle: compact([
      packet.episode.sourceName,
      packet.episode.publishedAt,
      `${packet.claims.length} claims`,
      `${packet.markets.length} market links`,
    ]),
    body: signalBody(packet),
    url: packet.episode.url,
    navigation: {
      view: "sources",
      targetId: `source-${packet.episode.sourceId}`,
    },
  }));

  const claimRecords: KnowledgeRecord[] = signalPackets.flatMap((packet) =>
    packet.claims.map((claim) => ({
      id: `claim:${claim.id}`,
      kind: "claim" as const,
      entityId: claim.id,
      title: claim.text,
      subtitle: compact([
        packet.episode.sourceName,
        claim.speaker.name,
        claim.kind,
        `${claim.review.status} · ${claim.review.confidence}`,
        `verify ${claim.timeline.endDate.slice(0, 10)}`,
      ]),
      body: claimBody(packet, claim),
      url: claim.sourceUrl,
      navigation: {
        view: "timeline" as const,
        timelineMode: "verification" as const,
        targetId: `timeline-${encodeURIComponent(`claim:${claim.id}`)}`,
      },
    })),
  );

  const systemRecords: KnowledgeRecord[] = systems.map((system) => ({
    id: `system:${system.id}`,
    kind: "system",
    entityId: system.id,
    title: system.name,
    subtitle: compact([system.type, system.scope]),
    body: systemBody(system),
    navigation: {
      view: "systems",
      targetId: `system-${system.id}`,
    },
  }));

  const simulationRecords: KnowledgeRecord[] = simulationModels.map((model) => {
    const run = simulationRuns.find((candidate) => candidate.modelId === model.id);
    return {
      id: `simulation:${model.id}`,
      kind: "simulation",
      entityId: model.id,
      title: model.name,
      subtitle: compact([
        model.kind,
        `${model.horizonDays}-day horizon`,
        `${model.particleCount.toLocaleString()} particles`,
      ]),
      body: simulationBody(model, run),
      navigation: {
        view: "simulations",
        targetId: `simulation-${model.id}`,
      },
    };
  });

  const projectionRecords: KnowledgeRecord[] = projectionSets.flatMap((set) => {
    const setRecord: KnowledgeRecord = {
      id: `projection:${set.id}`,
      kind: "projection",
      entityId: set.id,
      title: set.title,
      subtitle: compact([
        set.family,
        `${set.horizonDays}-day forecast set`,
        `${set.trajectories.length} terminal regimes`,
      ]),
      body: projectionBody(set),
      navigation: {
        view: "timeline",
        timelineMode: "projected",
        targetId: `projection-${set.id}`,
      },
    };
    const trajectoryRecords: KnowledgeRecord[] = set.trajectories.map((trajectory) => ({
      id: `projection:${set.id}:${trajectory.id}`,
      kind: "projection",
      entityId: trajectory.id,
      title: trajectory.title,
      subtitle: compact([
        set.title,
        `${(trajectory.probabilityBps / 100).toFixed(2)}% probability`,
        `${set.horizonDays}-day terminal regime`,
      ]),
      body: projectionBody(set, trajectory),
      navigation: {
        view: "timeline",
        timelineMode: "projected",
        targetId: `projection-${trajectory.id}`,
      },
    }));
    return [setRecord, ...trajectoryRecords];
  });

  const timelineRecords: KnowledgeRecord[] = indexedTimelineEvents.map((event) => ({
    id: `timeline:${event.id}`,
    kind: "timeline",
    entityId: event.id,
    title: event.title,
    subtitle: compact([event.at, event.kind, event.sourceIds.join(" + ")]),
    body: compact([event.detail, event.sourceIds.join(" ")]),
    url: event.url,
    navigation: {
      view: "timeline",
      timelineMode: event.id.startsWith("claim:") ? "verification" : "observed",
      timelineLens: event.id.startsWith("claim:") ? undefined : event.domain ?? "unified",
      targetId: `timeline-${encodeURIComponent(event.id)}`,
    },
  }));

  const portfolioRecords: KnowledgeRecord[] = portfolioPositions.map((position) => ({
    id: `portfolio:${position.id}`,
    kind: "portfolio",
    entityId: position.id,
    title: `${position.symbol} · ${position.name}`,
    subtitle: compact([
      position.origin === "webull-paper"
        ? "Webull PaperTrade"
        : position.origin === "polymarket"
          ? "Polymarket real"
          : position.origin === "polymarket-paper" || position.origin === "manual-paper"
            ? "Polymarket simulated"
            : position.origin === "kalshi-demo" || position.origin === "kalshi-paper"
              ? "Kalshi paper"
              : position.origin === "kalshi-production"
                ? "Kalshi real"
                : "Owner entered",
      position.status,
      position.side,
      position.assetType,
      position.venue,
    ]),
    body: portfolioBody(position),
    navigation: {
      view: "portfolio",
      targetId: `portfolio-${position.id}`,
    },
  }));

  const mediaBody = (draft: MediaDraft) => {
    if (draft.type === "article") {
      return [draft.dek, draft.thesis, ...Object.values(draft.sections).flat()];
    }
    switch (draft.type) {
      case "x-post":
      case "threads-post":
        return [draft.content.visualDirection, ...draft.content.posts.map((post) => post.text)];
      case "instagram-post":
        return [
          draft.content.visualConcept,
          draft.content.caption,
          draft.content.altText,
          ...draft.content.hashtags,
          ...draft.content.slides.flatMap((slide) => [slide.headline, slide.body]),
        ];
      case "youtube-video":
        return [
          draft.content.thumbnailText,
          draft.content.description,
          draft.content.hook,
          ...draft.content.sections.flatMap((section) => [section.heading, section.narration, section.visualDirection]),
          draft.content.close,
        ];
      case "instagram-reel":
        return [
          draft.content.coverText,
          draft.content.audioDirection,
          draft.content.caption,
          ...draft.content.hashtags,
          ...draft.content.beats.flatMap((beat) => [beat.spokenLine, beat.onScreenText, beat.visualDirection]),
        ];
      case "youtube-short":
        return [
          draft.content.caption,
          ...draft.content.beats.flatMap((beat) => [beat.spokenLine, beat.onScreenText, beat.visualDirection]),
        ];
      case "tiktok":
        return [
          draft.content.soundDirection,
          draft.content.caption,
          ...draft.content.hashtags,
          ...draft.content.beats.flatMap((beat) => [beat.spokenLine, beat.onScreenText, beat.visualDirection]),
        ];
      case "youtube-live":
        return [
          draft.content.thumbnailText,
          draft.content.description,
          draft.content.pinnedChatPrompt,
          draft.content.moderatorBrief,
          ...draft.content.preShowChecklist,
          draft.content.replayPlan,
          ...draft.content.segments.flatMap((segment) => [segment.heading, segment.hostNotes, segment.visualDirection, segment.audiencePrompt]),
        ];
      case "twitch-live":
        return [
          draft.content.description,
          draft.content.category,
          ...draft.content.tags,
          draft.content.chatPrompt,
          draft.content.moderatorBrief,
          ...draft.content.preShowChecklist,
          draft.content.sceneDirection,
          draft.content.clipPlan,
          ...draft.content.segments.flatMap((segment) => [segment.heading, segment.hostNotes, segment.visualDirection, segment.audiencePrompt]),
        ];
    }
  };

  const mediaRecords: KnowledgeRecord[] = mediaDrafts.map((draft) => ({
    id: `media:${draft.id}`,
    kind: "media",
    entityId: draft.id,
    title: draft.title,
    subtitle: compact([
      `${draft.type.replaceAll("-", " ")} draft`,
      draft.generatedAt,
      `${draft.citations.length} citations`,
      `${draft.confidence} confidence`,
    ]),
    body: [
      draft.objective,
      ...mediaBody(draft),
      ...draft.limitations,
      ...draft.citations.flatMap((citation) => [
        citation.recordId,
        citation.title,
        citation.observation,
      ]),
    ].join("\n"),
    navigation: {
      view: "media",
      targetId: `media-draft-${draft.id}`,
    },
  }));

  return [
    ...sourceRecords,
    ...signalRecords,
    ...claimRecords,
    ...systemRecords,
    ...simulationRecords,
    ...projectionRecords,
    ...timelineRecords,
    ...portfolioRecords,
    ...mediaRecords,
  ];
}

function normalize(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function containsToken(value: string, token: string) {
  if (token.length <= 2) return value.split(" ").includes(token);
  if (value.includes(token)) return true;
  return token.length > 3 && token.endsWith("s") && value.includes(token.slice(0, -1));
}

function searchScore(record: KnowledgeRecord, tokens: string[], normalizedQuery: string) {
  const title = normalize(record.title);
  const subtitle = normalize(record.subtitle);
  const body = normalize(record.body);
  const entityId = normalize(record.entityId);

  const combined = `${record.kind} ${title} ${subtitle} ${body} ${entityId}`;
  if (!tokens.every((token) => containsToken(combined, token))) {
    return -1;
  }

  let score = 0;
  if (title === normalizedQuery) score += 120;
  if (entityId === normalizedQuery) score += 110;
  if (title.startsWith(normalizedQuery)) score += 70;
  if (title.includes(normalizedQuery)) score += 50;
  if (subtitle.includes(normalizedQuery)) score += 20;

  tokens.forEach((token) => {
    if (containsToken(title, token)) score += 16;
    if (containsToken(entityId, token)) score += 12;
    if (containsToken(subtitle, token)) score += 6;
    if (containsToken(body, token)) score += 1;
  });

  return score;
}

export function searchKnowledge(
  records: KnowledgeRecord[],
  query: string,
  options: KnowledgeSearchOptions = {},
): KnowledgeSearchResult {
  const normalizedQuery = normalize(query);
  const tokens = normalizedQuery.split(" ").filter(Boolean);
  const kinds = options.kinds?.length ? new Set(options.kinds) : null;
  const limit = Math.min(Math.max(options.limit ?? 12, 1), 100);
  const offset = Math.max(options.offset ?? 0, 0);

  const ranked = records
    .filter((record) => !kinds || kinds.has(record.kind))
    .map((record) => ({
      record,
      score: tokens.length ? searchScore(record, tokens, normalizedQuery) : 0,
    }))
    .filter(({ score }) => score >= 0)
    .sort((left, right) =>
      right.score - left.score || left.record.title.localeCompare(right.record.title),
    );

  return {
    query,
    total: ranked.length,
    offset,
    limit,
    results: ranked.slice(offset, offset + limit).map(({ record }) => record),
  };
}

export function getSourceById(connectors: ConnectorSnapshot[], id: ConnectorId | string) {
  return connectors.find((connector) => connector.id === id) ?? null;
}

export function getSystemById(id: SystemId | string) {
  return systems.find((system) => system.id === id) ?? null;
}

export function getSimulationById(runs: SimulationRun[], id: string) {
  const model = getSimulationModel(id);
  const run = getSimulationRun(runs, id);
  if (!model && !run) return null;
  const resolvedModel = model ?? (run ? getSimulationModel(run.modelId) : null);
  return resolvedModel ? { model: resolvedModel, run } : null;
}

export function getProjectionById(sets: ProjectionSet[], id: string) {
  return findProjectionById(sets, id);
}
